import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { setTime, wait } from 'Src/me/pages/guest/proposals/new/utils';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import type { FeaturePropsType } from '../../../../../chat/views/types';
import type { RenderRadioLikeButtonGroupWithImagePropsType } from '../../../../../chat/views/RadioLikeButtonGroupWithImage/index';
import { scrollToBottomOfChat } from '../../../../../chat/functional/scrollToBottomOfChat';
import { RadioLikeButtonWithImage } from './components/RadioLikeButtonWithImage';
import { OtherButton } from './components/OtherButton';

const { tablet, mobile } = mediaQueries();

export type RadioLikeButtonGroupWithImagePropsType = FeaturePropsType<RenderRadioLikeButtonGroupWithImagePropsType>

export const RadioLikeButtonGroupWithImage = (props: RadioLikeButtonGroupWithImagePropsType) => {
  const {
    isInstant,
    doesNotScroll,
    enableHideAnimation: enableHideAnimationFromProps = true,
    shouldEnableHideAnimation,
    continueHandler,
    answers,
  } = props;
  const [isAnswered, setIsAnswered] = React.useState(false);
  const [showAnswerPanel, setShowAnswerPanel] = React.useState(true);
  const [isShow, setIsShow] = React.useState(isInstant);
  const [enableHideAnimation, setEnableHideAnimation] = React.useState(enableHideAnimationFromProps);
  const hasImageAnswers = answers.filter((answer) => answer.imagePaths);
  const simpleAnswers = answers.filter((answer) => !answer.imagePaths);

  React.useEffect(() => {
    (async () => {
      if (!isInstant) {
        await wait(0.4);

        setIsShow(true);
      }
    })();
  }, [isInstant]);

  React.useEffect(() => {
    if (!doesNotScroll) scrollToBottomOfChat();
  }, [doesNotScroll]);

  return (
    <div
      css={css`
        display: grid;
        grid-template-rows: minmax(0, 1fr);
        transition: grid-template-rows ${setTime(0.4)}s;

        ${!showAnswerPanel && css`
          overflow: hidden;
          grid-template-rows: minmax(0, 0fr);

          ${!enableHideAnimation && css`
            display: none;
          `}
        `}
      `}
    >
      <div
        css={css`
          padding: 30px 12px;
          position: relative;
          opacity: 0;
          bottom: -10px;
          transition: opacity ${setTime(0.4)}s ${setTime(0.4)}s, bottom ${setTime(0.4)}s ${setTime(0.4)}s;

          ${isShow && css`
            opacity: 1;
            bottom: 0;
          `}
        `}
      >
        <div
          css={css`
            max-width: 476px;
            margin-inline: auto;
            padding: 14px;
            background-color: #bae8e8;

            ${mobile(({ clamp }) => css`
              padding: ${clamp('8px', '14px')};
            `)}
          `}
        >
          <div
            css={css`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px 12px;
            `}
          >
            {hasImageAnswers.map((answer) => {
              const { id, answerHandler } = answer;
              const onClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
                const { value: currentValue, textContent } = event.currentTarget;

                // 回答済みフラグを立てる
                setIsAnswered(true);

                // 個別の回答処理を実行
                answerHandler(event);

                // 回答によって非表示のアニメーションを無効にする処理
                if (shouldEnableHideAnimation) {
                  setEnableHideAnimation(shouldEnableHideAnimation(textContent));
                }

                await wait(0.5);

                // 回答パネルを非表示にする
                setShowAnswerPanel(false);

                if (enableHideAnimation) await wait(0.5);

                // 回答内容を渡して次の処理に進む
                continueHandler({
                  value: currentValue,
                  answerText: textContent,
                  selfDomReferences: [],
                });
              };

              return (
                <RadioLikeButtonWithImage
                  key={id}
                  {...props}
                  {...answer}
                  isAnswered={isAnswered}
                  onClickHandler={onClickHandler}
                />
              );
            })}
          </div>
          {simpleAnswers.length > 0 && (
            <div
              css={css`
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 16px 12px;
                padding-top: 20px;
              `}
            >
              {simpleAnswers.map((answer) => {
                const { id, answerHandler } = answer;
                const onClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
                  const { value: currentValue, textContent } = event.currentTarget;

                  // 回答済みフラグを立てる
                  setIsAnswered(true);

                  // 個別の回答処理を実行
                  answerHandler(event);

                  // 回答によって非表示のアニメーションを無効にする処理
                  if (shouldEnableHideAnimation) {
                    setEnableHideAnimation(shouldEnableHideAnimation(textContent));
                  }

                  await wait(0.5);

                  // 回答パネルを非表示にする
                  setShowAnswerPanel(false);

                  if (enableHideAnimation) await wait(0.5);

                  // 回答内容を渡して次の処理に進む
                  continueHandler({
                    value: currentValue,
                    answerText: textContent,
                    selfDomReferences: [],
                  });
                };

                return (
                  <OtherButton
                    key={id}
                    {...props}
                    {...answer}
                    isAnswered={isAnswered}
                    onClickHandler={onClickHandler}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
